// The itinerary's hero IMAGE key — one rule, two readers.
//
// An itinerary's photo lives in the active version's `attachments` jsonb (the
// Photos settings tab). Two surfaces resolve it independently and MUST agree,
// or the storefront shows a different picture than the CMS row it synced from:
//   - `apps/cms/api/src/sync/service.ts` writes it to the CMS DB's `hero_image`
//   - `apps/itineraries/api/src/search/service.ts` projects it as
//     `heroImageKey` for the storefront's itinerary cards
// Both call `pickItineraryHeroImageKey`; the search query only *gathers* the
// candidates in SQL (cheaply — the raw column averages 3.5 KB/row and the XLS
// export pulls 10 000 rows through the same query), it never re-implements the
// choice.
//
// Distinct from the hero VIDEO (`itinerary.regional_video_id` →
// `regional_video.uploadthing_key`), which is a `video/mp4` key: usable as a
// `<video>` source, never as an `<img>` — feeding it to a card's `<img>` is
// what blanked every itinerary thumbnail.

export type ItineraryHeroImageSource = {
  // The active version's `attachments` — the raw jsonb array, or the compact
  // `{ type, fileKey, isThumbnail, showExternally, order }` projection the
  // search query builds from it.
  attachments: unknown;
  // Fallback when the Photos tab is empty: the itinerary's customer-visible
  // items that carry a thumbnail, as `{ contentCategory, thumbnail }` rows
  // ALREADY ordered by (start_time, id) — the caller's SQL gathers them, the
  // tier choice below is made here. A thumbnail is an uploadthing key, same as
  // an attachment key: both legacy `<uuid>-<slug>.jpg` and opaque keys resolve.
  fallbackItems?: unknown;
};

type Candidate = {
  fileKey: string;
  isThumbnail: boolean;
  order: number;
};

const nonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

// Attachments are free-form JSONB: an entry is a usable hero candidate when it
// is an IMAGE, carries a file key, and is not hidden from customers
// (`showExternally: false` — an image the consultant deliberately kept out of
// the customer-facing surfaces must not become the public card).
const toCandidate = (value: unknown, index: number): Candidate | null => {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (row.type !== 'IMAGE') return null;
  if (row.showExternally === false) return null;
  const fileKey = nonEmptyString(row.fileKey);
  if (!fileKey) return null;
  return {
    fileKey,
    isThumbnail: row.isThumbnail === true,
    // The Photos tab renders images sorted by `order` and rewrites it on every
    // reorder, so "first" must mean first ON SCREEN. Array position is the
    // tiebreak for rows whose `order` was never written.
    order: typeof row.order === 'number' ? row.order : index,
  };
};

/**
 * Which item's photo stands in for a missing curated one, best tier first: an
 * itinerary sells its experiences, so an Activity/Attraction photo represents
 * the trip better than a hotel façade, which still beats a transfer van. Any
 * category not listed here ranks last rather than being excluded — a card that
 * has a picture today must not regress to a grey placeholder.
 *
 * `Activity + Attraction` is the same "Experience" grouping Operations uses
 * (`EXPERIENCE_OPERATION_CATEGORIES`), NOT the pricing `EXPERIENCE_CATEGORIES`,
 * which also counts Dining.
 *
 * `skipFirst` drops a tier's earliest item when the tier has another to offer:
 * a trip's first "experience" is routinely arrival logistics filed under
 * Activity — VIP airport assistance, meet & greet — which is the worst possible
 * cover photo for the trip it opens.
 *
 * Exported with `gatherLimit` so a caller can gather that many candidates per
 * tier instead of every item (`apps/itineraries/api/src/search/service.ts` —
 * reading every item's heap tuple costs ~40 s on the 10 000-row XLS export).
 * Ranking still happens here; the short gather is equivalent because no row
 * past the `skipFirst` one can ever win its tier.
 */
export const ITINERARY_HERO_ITEM_CATEGORY_TIERS = [
  { categories: ['Activity', 'Attraction'], skipFirst: true, gatherLimit: 2 },
  { categories: ['Accommodation'], skipFirst: false, gatherLimit: 1 },
] as const satisfies readonly {
  categories: readonly string[];
  skipFirst: boolean;
  gatherLimit: number;
}[];

/**
 * An item whose title contains one of these is never the card's photo, whatever
 * its category: these name a logistics service, not something a traveller books
 * the trip to see. Matched case-insensitively, as a substring, against every
 * language of the item's jsonb title.
 *
 * The trade-off is deliberate and known: this also drops a genuinely scenic
 * "Private guided tour…", so it only ever costs the itinerary a *different*
 * photo — the last-resort tier still yields one — unless every candidate the
 * itinerary has is filtered out.
 *
 * Applied by both readers when they gather (`itineraries.search`, the CMS
 * sync), not here: a gather that returns only the earliest row per tier must
 * not spend that row on an excluded item.
 */
export const ITINERARY_HERO_EXCLUDED_TITLE_WORDS = [
  'transfer',
  'private',
] as const;

// Items of any other category share one last-resort tier, gathered by the same
// callers as a single earliest row.
const OTHER_TIER = ITINERARY_HERO_ITEM_CATEGORY_TIERS.length;

const categoryTier = (value: unknown): number => {
  const category = nonEmptyString(value);
  const tier = category
    ? ITINERARY_HERO_ITEM_CATEGORY_TIERS.findIndex((tier) =>
        (tier.categories as readonly string[]).includes(category)
      )
    : -1;
  return tier === -1 ? OTHER_TIER : tier;
};

// Rows arrive in day order, so a tier's bucket is that tier's items earliest
// first — `skipFirst` then reads the second one instead of the arrival-day one.
const pickFallbackThumbnail = (fallbackItems: unknown): string | null => {
  if (!Array.isArray(fallbackItems)) return null;
  const buckets: string[][] = Array.from({ length: OTHER_TIER + 1 }, () => []);
  for (const value of fallbackItems) {
    if (!value || typeof value !== 'object') continue;
    const row = value as Record<string, unknown>;
    const thumbnail = nonEmptyString(row.thumbnail);
    if (!thumbnail) continue;
    buckets[categoryTier(row.contentCategory)].push(thumbnail);
  }

  for (const [tier, bucket] of buckets.entries()) {
    if (bucket.length === 0) continue;
    const skipFirst =
      ITINERARY_HERO_ITEM_CATEGORY_TIERS[tier]?.skipFirst === true &&
      bucket.length > 1;
    return skipFirst ? bucket[1] : bucket[0];
  }
  return null;
};

/**
 * The photo an itinerary card shows, in priority order:
 *   1. the Photos-tab image flagged as thumbnail
 *   2. else the first Photos-tab image (as ordered in that tab)
 *   3. else an item thumbnail — a trip with products but no curated photo still
 *      gets a relevant picture instead of a grey box. An Activity/Attraction
 *      item wins (the second one, unless it is the only one — see
 *      `skipFirst`), else the earliest Accommodation one, else the earliest
 *      item of any other category.
 *   4. else null → the caller renders its placeholder
 *
 * Images hidden from customers (`showExternally: false`) are never used.
 */
export const pickItineraryHeroImageKey = ({
  attachments,
  fallbackItems,
}: ItineraryHeroImageSource): string | null => {
  const candidates: Candidate[] = [];
  if (Array.isArray(attachments)) {
    attachments.forEach((value, index) => {
      const candidate = toCandidate(value, index);
      if (candidate) candidates.push(candidate);
    });
    candidates.sort((a, b) => a.order - b.order);
  }

  const flagged = candidates.find((candidate) => candidate.isThumbnail);
  if (flagged) return flagged.fileKey;
  const first = candidates[0];
  if (first) return first.fileKey;

  return pickFallbackThumbnail(fallbackItems);
};
